import React from "react";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericMultiDropdown from "@ui-components/Dropdown/GenericMultiDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { getServiceDropdownLabel } from "../../utils/tickets";
import type { UseChangeRequestFormReturn } from "../../hooks/useChangeRequestForm";
import type { CustomerInfo, LabelValue, ServiceItem } from "../../utils/tickets";

interface ServiceSelectionTabProps {
  isKontrolEdge: boolean;
  isPartnerView: boolean;
  form: UseChangeRequestFormReturn;
  uniqueCustomers: CustomerInfo[];
  filteredServiceItems: ServiceItem[];
  topics: LabelValue[];
  selectedTopicLabel: string;
  onCustomerSelect: (customerNameDisplay: string) => void;
  onServiceSelect: (selectedLabels: string[]) => void;
  onSelectTopic: (topicLabel: string) => void;
  onServicesDropdownChange: (label: string) => void;
  getServicesLabel: () => string;
  isGeneralInfoValid: () => boolean;
  onNext: () => void;
}

export const ServiceSelectionTab: React.FC<ServiceSelectionTabProps> = ({
  isKontrolEdge,
  isPartnerView,
  form,
  uniqueCustomers,
  filteredServiceItems,
  topics,
  selectedTopicLabel,
  onCustomerSelect,
  onServiceSelect,
  onSelectTopic,
  onServicesDropdownChange,
  getServicesLabel,
  isGeneralInfoValid,
  onNext,
}) => {
  return (
    <div className="space-y-4 text-sm text-semantic-text-secondary flex flex-col h-full">
      {isKontrolEdge && (
        <>
          {/* Customer selector (only in partner view, full width on top) */}
          {isPartnerView && (
            <div>
              <label className="block mb-1 font-medium text-semantic-text-secondary">
                Select Customer <span className="text-red-500">*</span>
              </label>
              <GenericDropdown
                items={uniqueCustomers.map((c) => c.nameDisplay)}
                value={form.formState.selectedCustomerNameDisplay}
                onSelect={onCustomerSelect}
                defaultLabel="Select a customer"
                minWidthClass=""
                maxWidthClass=""
                widthClass="w-full"
                dropdownMaxHeightClass="max-h-48"
                fixedPosition={true}
              />
            </div>
          )}
          {/* Services and Service disruptions: stacked on mobile, side by side on sm+ */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="w-full sm:w-1/2">
              <label className="block mb-1 font-medium text-semantic-text-secondary flex items-center gap-1">
                Select Service(s) <span className="text-red-500">*</span>
                <GenericTooltip
                  text="You can select multiple services"
                  position="top"
                />
              </label>
              <GenericMultiDropdown
                items={filteredServiceItems.map(getServiceDropdownLabel)}
                value={form.formState.selectedServiceLabels}
                onSelect={onServiceSelect}
                defaultLabel="Select service(s)"
                minWidthClass=""
                maxWidthClass=""
                widthClass="w-full"
                disabled={isPartnerView && !form.formState.selectedCustomerId}
                fixedPosition={true}
                dropdownMaxHeightClass="max-h-48"
              />
            </div>
            <div className="w-full sm:w-1/2">
              <label className="block mb-1 font-medium text-semantic-text-secondary">
                Are there service disruptions?{" "}
                <span className="text-red-500">*</span>
              </label>
              <GenericDropdown
                items={["Yes", "No"]}
                value={getServicesLabel()}
                onSelect={onServicesDropdownChange}
                defaultLabel="Select"
                minWidthClass=""
                maxWidthClass=""
                widthClass="w-full"
                fixedPosition={true}
                dropdownMaxHeightClass="max-h-48"
              />
            </div>
          </div>
        </>
      )}

      {!isKontrolEdge && (
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="w-full sm:w-1/2">
            <label className="block mb-1 font-medium">
              Topic <span className="text-red-500">*</span>
            </label>
            <GenericDropdown
              items={topics.map((t) => t.label)}
              value={selectedTopicLabel}
              onSelect={onSelectTopic}
              defaultLabel="No options available"
              minWidthClass=""
              maxWidthClass=""
              widthClass="w-full"
              fixedPosition={true}
              dropdownMaxHeightClass="max-h-48"
            />
          </div>
          <div className="w-full sm:w-1/2">
            <label className="block mb-1 font-medium">
              Are there service disruptions?{" "}
              <span className="text-red-500">*</span>
            </label>
            <GenericDropdown
              items={["Yes", "No"]}
              value={getServicesLabel()}
              onSelect={onServicesDropdownChange}
              defaultLabel="Select"
              minWidthClass=""
              maxWidthClass=""
              widthClass="w-full"
              fixedPosition={true}
              dropdownMaxHeightClass="max-h-48"
            />
          </div>
        </div>
      )}

      {/* Buttons fixed at bottom */}
      <div className="sticky bottom-0 bg-[var(--color-semantic-bg-primary)] pt-4 pb-2 border-t border-gray-200 -mx-4 px-4 mt-auto">
        <div className="flex justify-end gap-2">
          <GenericButton
            label="Next"
            variant="brand"
            onClick={onNext}
            disabled={!isGeneralInfoValid()}
          />
        </div>
      </div>
    </div>
  );
};

